import { type RefObject, useEffect } from 'react';
import { appContext } from '../../app/context';
import { openAnyFile } from '../../app/documentIO';
import { imageFilesFrom } from '../../io/imageIO';
import { useEditorStore } from '../../state/editorStore';

/**
 * Getting images in without a dialog: drop them on the stage, or paste from the
 * clipboard anywhere in the app. Both funnel into the same open flow, so the
 * oversize rules apply identically.
 */
export function useImageImport(stageRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const open = async (files: File[]) => {
      if (files.length === 0) return;
      const store = useEditorStore.getState();
      try {
        const outcome = await openAnyFile(files[0] as File, appContext);
        if (!outcome.ok && outcome.reason !== 'Cancelled') {
          store.notify(outcome.reason ?? 'Could not open that image', 'error');
        } else if (outcome.ok) {
          store.notify(`Opened ${outcome.document?.name}`);
        }
      } catch (error) {
        store.notify(error instanceof Error ? error.message : 'Could not open that image', 'error');
      }
    };

    const stage = stageRef.current;
    const onDragOver = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (event: DragEvent) => {
      // The stage is a drop target, so a drop here must never fall through to
      // the browser — a non-image file would otherwise navigate the tab away,
      // autosave window and all.
      event.preventDefault();
      const files = imageFilesFrom(event.dataTransfer);
      if (files.length === 0) return;
      void open(files);
    };

    const onPaste = (event: ClipboardEvent) => {
      const files = imageFilesFrom(event.clipboardData);
      if (files.length === 0) return;
      event.preventDefault();
      void open(files);
    };

    // A file dropped on a panel or a menu must not navigate either. `dragover`
    // has to be cancelled for the browser to fire `drop` at all — without it,
    // the tab just loads the file — but only file drags are stood in for, so
    // dragging text between inputs keeps working.
    const isFileDrag = (event: DragEvent): boolean =>
      event.dataTransfer?.types.includes('Files') ?? false;
    const guardDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
    };
    const guardDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
    };

    stage?.addEventListener('dragover', onDragOver);
    stage?.addEventListener('drop', onDrop);
    window.addEventListener('dragover', guardDragOver);
    window.addEventListener('drop', guardDrop);
    window.addEventListener('paste', onPaste);
    return () => {
      stage?.removeEventListener('dragover', onDragOver);
      stage?.removeEventListener('drop', onDrop);
      window.removeEventListener('dragover', guardDragOver);
      window.removeEventListener('drop', guardDrop);
      window.removeEventListener('paste', onPaste);
    };
  }, [stageRef]);
}
